import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Repeat, Pause, Play, XCircle, Plus, Loader2 } from "lucide-react";
import { SubscriptionDetailDialog } from "@/components/admin/schedule/SubscriptionDetailDialog";

// dayNames moved inside component to use t()

export default function AdminRecurring() {
  const { t } = useTranslation("admin");
  const dayNames = [t("common.sunday"), t("common.monday"), t("common.tuesday"), t("common.wednesday"), t("common.thursday"), t("common.friday"), t("common.saturday")];
  const { organization } = useOrganization();
  const { hallId } = useOrgHall();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editEntry, setEditEntry] = useState<any | null>(null);
  // G-032: ending a recurring series is destructive — confirm first.
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  // Form state
  const [userId, setUserId] = useState("");
  const [courtId, setCourtId] = useState("");
  const [dayOfWeek, setDayOfWeek] = useState("1");
  const [startTime, setStartTime] = useState("18:00");
  const [endTime, setEndTime] = useState("19:30");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["admin-recurring", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recurring_bookings")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("day_of_week")
        .order("start_time");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: courts = [] } = useQuery({
    queryKey: ["admin-courts-list", hallId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courts")
        .select("id, name")
        .eq("hall_id", hallId!)
        .eq("status", "active")
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hallId,
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["admin-profiles-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .order("full_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch customer names for entries with customer_id
  const customerIds = useMemo(
    () => [...new Set(((entries ?? []) as any[]).map((e) => (e as any).customer_id).filter(Boolean))],
    [entries],
  );
  const { data: customers = [] } = useQuery({
    queryKey: ["admin-recurring-customers", customerIds],
    queryFn: async () => {
      if (customerIds.length === 0) return [];
      const { data } = await supabase.from("customers").select("id, full_name, email").in("id", customerIds);
      return data ?? [];
    },
    enabled: customerIds.length > 0,
  });

  // Enrich entries with profile/court/customer names
  const profileMap = new Map(profiles.map((p) => [p.id, p.full_name]));
  const courtMap = new Map(courts.map((c) => [c.id, c.name]));
  const customerMap = new Map(((customers ?? []) as any[]).map((c) => [c.id, c.full_name || c.email || "Kund"]));

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("recurring_bookings").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-recurring"] });
      toast.success(t("admin:toast.statusUpdated"));
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("recurring_bookings").insert({
        organization_id: organization!.id,
        user_id: userId,
        court_id: courtId,
        day_of_week: parseInt(dayOfWeek),
        start_time: startTime,
        end_time: endTime,
        valid_from: validFrom,
        valid_until: validUntil,
        status: "active",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-recurring"] });
      toast.success(t("admin:toast.recurringCreated"));
      setShowCreate(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Skeleton className="h-40" />;

  const active = entries.filter((e) => e.status !== "cancelled");

  // Spår S1 — group by logical-series key: (customer_id ?? user_id) +
  // court_id + day_of_week + start_time. After a split-edit the same
  // logical series is two recurring_bookings rows (historic + current);
  // grouping shows them as one item with multiple periods.
  type SeriesEntry = typeof active[number];
  const seriesGroups = (() => {
    const map = new Map<string, SeriesEntry[]>();
    for (const e of active) {
      const key = `${(e as any).customer_id ?? e.user_id ?? "anon"}|${e.court_id}|${e.day_of_week}|${e.start_time}`;
      (map.get(key) ?? map.set(key, []).get(key)!).push(e);
    }
    // Sort each group's rows ascending by valid_from so periods read chronologically.
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.valid_from ?? "").localeCompare(b.valid_from ?? ""));
    }
    return Array.from(map.values());
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Repeat className="h-4 w-4" /> {t("recurring.title")} ({seriesGroups.length})
        </h3>
        <Button size="sm" className="gap-1" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> {t("recurring.createNew")}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{t("recurring.subtitle")}</p>

      {seriesGroups.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">{t("recurring.noEntries")}</p>
      ) : (
        seriesGroups.map((group) => {
          // Click → edit the CURRENT period (last in sorted array) which is
          // the in-effect-or-future row. Status actions also operate on the
          // current row only (historic periods are immutable post-split).
          const current = group[group.length - 1];
          const hasMultiplePeriods = group.length > 1;
          return (
            <Card key={current.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setEditEntry(current)}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{profileMap.get(current.user_id) || customerMap.get((current as any).customer_id) || (current.user_id ? current.user_id.slice(0, 8) : (current as any).comment || "Abonnemang")}</p>
                  <p className="text-xs text-muted-foreground">
                    {dayNames[current.day_of_week]} · {courtMap.get(current.court_id) || "Bana"} · {current.start_time?.slice(0, 5)}-{current.end_time?.slice(0, 5)}
                  </p>
                  {hasMultiplePeriods ? (
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium">{t("subscription.multiplePeriods", { count: group.length, defaultValue: `${group.length} perioder` })}: </span>
                      {group.map((p, idx) => (
                        <span key={p.id}>
                          {idx > 0 && " · "}
                          {format(new Date(p.valid_from), "d MMM", { locale: sv })}
                          –{format(new Date(p.valid_until), "d MMM yyyy", { locale: sv })}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(current.valid_from), "d MMM", { locale: sv })} – {format(new Date(current.valid_until), "d MMM yyyy", { locale: sv })}
                    </p>
                  )}
                </div>
                <Badge variant={current.status === "active" ? "default" : "secondary"}>
                  {current.status === "active" ? t("common.active") : t("recurring.paused")}
                </Badge>
                <div className="flex gap-1">
                  {current.status === "active" ? (
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); updateStatusMutation.mutate({ id: current.id, status: "paused" }); }} title={t("recurring.pause")}>
                      <Pause className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); updateStatusMutation.mutate({ id: current.id, status: "active" }); }} title={t("recurring.activate")}>
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setConfirmCancelId(current.id); }} title={t("recurring.cancel")}>
                    <XCircle className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* G-032: confirm before ending a recurring series */}
      <AlertDialog open={!!confirmCancelId} onOpenChange={(open) => { if (!open) setConfirmCancelId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("recurring.confirmCancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("recurring.confirmCancelDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmCancelId) updateStatusMutation.mutate({ id: confirmCancelId, status: "cancelled" });
                setConfirmCancelId(null);
              }}
            >
              {t("recurring.cancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("recurring.newRecurringBooking")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("recurring.player")}</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger><SelectValue placeholder={t("recurring.selectPlayer")} /></SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name || p.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("recurring.weekday")}</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {dayNames.map((name, i) => (
                      <SelectItem key={i} value={String(i)}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("recurring.court")}</Label>
                <Select value={courtId} onValueChange={setCourtId}>
                  <SelectTrigger><SelectValue placeholder={t("recurring.selectCourt")} /></SelectTrigger>
                  <SelectContent>
                    {courts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("recurring.startTime")}</Label><Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
              <div className="space-y-2"><Label>{t("recurring.endTime")}</Label><Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("recurring.validFrom")}</Label><Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} /></div>
              <div className="space-y-2"><Label>{t("recurring.validUntil")}</Label><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !userId || !courtId || !validFrom || !validUntil}
            >
              {createMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : null}
              {t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit subscription dialog */}
      {editEntry && (
        <SubscriptionDetailDialog
          subscription={editEntry}
          open={!!editEntry}
          onOpenChange={(v) => { if (!v) setEditEntry(null); }}
          courts={courts}
          onUpdated={() => {
            qc.invalidateQueries({ queryKey: ["admin-recurring"] });
            setEditEntry(null);
          }}
          organizationId={organization?.id}
          customerName={customerMap.get((editEntry as any).customer_id)}
        />
      )}
    </div>
  );
}
